<template>
    <div
        ref="fulltextSearchRef"
        class="fulltext-search-container relative"
    >
        <FulltextSearchInput
            v-model="modelValueLocal"
            :placeholder="placeholder"
            :accessibility-label="accessibilityLabel"
            input-class="py-2"
            icon-class="w-4 h-4"
            @search-performed="$emit('search-performed')"
            @keydown.down.prevent="selectedIndex = (selectedIndex + 1) % searchResults.length"
            @keydown.up.prevent="selectedIndex = (selectedIndex - 1 + searchResults.length) % searchResults.length"
            @keydown.tab.prevent="selectedIndex = (selectedIndex + 1) % searchResults.length"
            @keydown.enter="onInputEnter"
        />

        <FulltextSearchResults
            :results="searchResults"
            :show-results="showSearchResults"
            :view-all-url="viewAllUrl"
            :view-all-text="viewAllText"
            :container-class="resultsContainerClass"
            :selected-index="selectedIndex"
            @result-selected="handleResultSelected"
            @view-all-clicked="emit('close-results', true)"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import { useVModel } from '@vueuse/core';
    import { useRouter } from 'vue-router';
    import FulltextSearchInput from './FulltextSearchInput.vue';
    import FulltextSearchResults from './FulltextSearchResults.vue';
    import { resultNavigationPath } from '~/utils/searchResultNavigation.ts';
    import type { SearchResult } from '~/types/fulltextSearch';

    type FulltextSearchContainerProps = {
        modelValue: string;
        placeholder: string;
        accessibilityLabel: string;
        viewAllUrl: string;
        viewAllText: string;
        showSearchResults: boolean;
        searchResults: SearchResult[];
        resultsContainerClass?: string;
        autoFocus?: boolean;
        ignoreClickOutside?: string[];
    };

    type FulltextSearchContainerEmits = {
        (e: 'update:modelValue', value: string): void;
        (e: 'result-selected', result: SearchResult): void;
        (e: 'search-performed'): void;
        (e: 'close-results', force?: boolean): void;
    };

    const props = withDefaults(defineProps<FulltextSearchContainerProps>(), {
        resultsContainerClass: 'absolute top-full left-0 right-0 mt-1',
        autoFocus: false,
        ignoreClickOutside: () => ['.fulltext-search-toggle-btn']
    });

    const emit = defineEmits<FulltextSearchContainerEmits>();

    const router = useRouter();
    const modelValueLocal = useVModel(props, 'modelValue', emit);
    const fulltextSearchRef = ref<HTMLElement | null>(null);
    const selectedIndex = ref(-1); // -1 means nothing select

    const handleResultSelected = (result: SearchResult) => {
        emit('result-selected', result);
    };

    const onInputEnter = () => {
        if (selectedIndex.value >= 0 && selectedIndex.value < props.searchResults.length) {
            const selected = props.searchResults[selectedIndex.value];
            const path = resultNavigationPath(selected);
            if (selected && path) {
                router.push(path);
                emit('close-results', true);
            }
        } else {
            navigateToSearchPage();
        }
    };

    const navigateToSearchPage = () => {
        // Navigate to the search page and close the dropdown
        if (!props.viewAllUrl) return;
        router.push(props.viewAllUrl);
        emit('close-results', true);
    };
</script> 
